import { useEffect, useRef, useState } from 'react';
import ContentItemsPanel from 'components/Dashboard/Analytics/ui/ContentItemsPanel';
import useRightPanelIndividualCall from 'components/Dashboard/Analytics/useRightPanelIndividualCall';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';

const InterestByRegionRightPanel = (props) => {
  const { contentState, analyticsState, setRightPanelState, allowFrontbenchers } = props;
  const { chartDataOptionSelected } = analyticsState;
  const { text, rawData, dataKey, region, contentType, noResults, totalRow } = contentState;

  const chartOptionName = chartDataOptionSelected;
  const totalCell = dataKey === 'totalCell';
  const contributionGraph = chartOptionName === 'Contribution type';
  const searchParameter = getUrlParam('search');

  // "Written questions" already matches what individualApiCall expects, so only the older
  // content-type labels that differ from the graph's section names need translating.
  const contentTypeMapping = {
    'Parliamentary contribution': 'Spoken parliamentary contributions',
    EDM: 'EDMs',
    Tweet: 'X post',
    Retweet: 'X repost',
  };

  const createSectionValues = () => {
    const values = [];
    if (!noResults) {
      if (totalCell) {
        if (totalRow) {
          Object.keys(rawData[region]).forEach((contentType) => {
            values.push({
              name: contentTypeMapping[contentType] ?? contentType,
              type: contentType,
              value: rawData[region][contentType],
            });
          });
        } else {
          // contentType arrives as [item] here (see InterestByRegionChartData's bottom-row
          // "Total" click, which needs it as an array for its own active-cell check) - unwrap it
          // so `type` below is a string, not an array (array.startsWith would throw downstream).
          const contentTypeValue = Array.isArray(contentType) ? contentType[0] : contentType;
          const value = Object.keys(rawData)
            .map((item) => rawData[item][contentTypeValue])
            .reduce((prev, cur) => prev + (cur ?? 0), 0);
          values.push({
            name: contentTypeMapping[contentTypeValue] ?? contentTypeValue,
            type: contentTypeValue,
            value: value,
          });
        }
      } else {
        values.push({
          name: contentTypeMapping[dataKey] ?? dataKey,
          type: dataKey,
          value: rawData[region][dataKey],
        });
      }
    }

    return values.filter((item) => item.value > 0);
  };

  const source = new AbortController();

  const sectionsValues = createSectionValues();
  const [activeSection, setActiveSection] = useState(
    contributionGraph && !totalCell ? dataKey : sectionsValues[0]?.name
  );
  const [isLoading, setIsLoading] = useState(!noResults);
  const [results, setResults] = useState([]);

  const callContentItems = useRef(null);
  const pageSize = parseInt(import.meta.env.VITE_APP_PAGE_SIZE);
  const isLoadedComponent = useRef(false);

  const { individualApiCall } = useRightPanelIndividualCall({
    analyticsState,
    requestSource: source,
    contentState,
    allowFrontbenchers,
  });

  callContentItems.current = async ({ type = activeSection, pageNumber, source }) => {
    try {
      if (noResults) {
        setResults({ hits: [] });
      } else {
        if (!pageNumber) {
          setIsLoading(true);
        }
        let snapshot = !!contentState?.sentiment;
        // `type` defaults to activeSection, which ContentItemsPanel sets from sectionsValues'
        // display name (e.g. "X post") - resolve it back to the raw content type ("Tweet") that
        // individualApiCall's type detection expects.
        const resolvedType = sectionsValues.find((item) => item.name === type)?.type ?? type;
        let callresults = await individualApiCall({
          type: resolvedType,
          pageNumber,
          snapshot,
          freeSearch: !!searchParameter ? decodeUrlQueryValue(searchParameter) : null,
          source,
        });
        if (callresults) {
          let hits = pageNumber ? results.hits : [];
          let highlights = pageNumber ? results.highlights : [];

          hits = hits.concat(callresults.hits);
          highlights = highlights.concat(callresults.highlights);

          setResults({
            ...callresults,
            hits,
            highlights,
            thereIsMoreContent: callresults.hits?.length === pageSize,
          });
          setIsLoading(false);
        }
      }
      isLoadedComponent.current = true;
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    callContentItems.current({ source });
    return () => {
      source.abort('Content items panel cancelled by the user');
    };
  }, [activeSection, allowFrontbenchers]);

  useEffect(() => {
    if (isLoadedComponent.current) {
      let valueToCompare = contributionGraph && !totalCell ? dataKey : sectionsValues[0]?.name;
      if (valueToCompare !== activeSection) {
        setActiveSection(valueToCompare);
      } else {
        callContentItems.current({});
      }
    }
  }, [contentState]);

  const load_more_results = async () => {
    let { thereIsMoreContent, pageNumber } = results;
    if (thereIsMoreContent) {
      await callContentItems.current({ pageNumber: pageNumber + 1 });
    }
  };
  const handleScroll = (scrollValues) => {
    const { scrollTop, clientHeight, scrollHeight } = scrollValues;
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
    if (scrolledToBottom) {
      load_more_results();
    }
  };

  return (
    <ContentItemsPanel
      handleScroll={handleScroll}
      setRightPanelState={setRightPanelState}
      text={text}
      sectionsValues={sectionsValues}
      isLoading={isLoading}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      results={results}
      interestByRegionPage
    />
  );
};

export default InterestByRegionRightPanel;
